import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ExternalAttachment from 'components/shared/ExternalAttachment';
import FileDropZone from 'components/shared/FileDropZone';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import Upload from 'hooks/useDirectUpload/Upload';
import useDirectUpload from 'hooks/useDirectUpload/useDirectUpload';
import { createReferralSourceImport, ReferralSourceImport } from 'services/api/admin/referralSourceImports';
import { validationErrorMessage } from 'services/api/validationErrors';
import { colors } from 'styles/theme/colors';
import ClipIcon from 'svg/AttachFileIcon';
import TrashIcon from 'svg/TrashIcon';

// ReferralSourceImport::Csv::ALLOWED_HEADERS: Referral Source, optionally followed by Also Known As.
const COLUMNS = [
  { header: 'Referral Source', optional: false },
  { header: 'Also Known As', optional: true },
];

// Headers only: every non-blank row imports, so an example row would land on the client's list.
const TEMPLATE_CSV = `${COLUMNS.map(({ header }) => header).join(',')}\n`;
const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_CSV)}`;

// Mirrors ReferralSourceImport::Importer::MAX_FILE_SIZE, which is the authoritative check.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
// The extension entry still accepts a .csv the OS labels application/vnd.ms-excel.
const CSV_TYPES = { 'text/csv': ['.csv'] };

function SmallTrashIcon(props: React.SVGProps<SVGSVGElement>) {
  return <TrashIcon {...props} width={12} height={12} />;
}

type Props = {
  clientId: string;
  onClose: () => void;
  onUploaded: (referralSourceImport: ReferralSourceImport) => void;
};

export default function UploadReferralSourcesModal({ clientId, onClose, onUploaded }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State updates too late to stop a fast double click from posting twice; a ref flips immediately.
  const submittingRef = useRef(false);

  const { uploads, uploadFiles, removeUpload } = useDirectUpload([]);
  const upload: Upload | undefined = uploads[0];
  const fileReady = !!upload?.isComplete && !!upload.signedId;
  const canUpload = fileReady && !submitting;

  const handleFiles = (files: File[]) => {
    setError(null);
    const file = files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('The file is larger than 10 MB.');
      return;
    }

    uploadFiles([file]);
  };

  const handleUpload = async () => {
    if (submittingRef.current || !canUpload || !upload) return;
    submittingRef.current = true;

    setSubmitting(true);
    setError(null);
    try {
      const created = await createReferralSourceImport({
        clientId,
        signedId: upload.signedId!,
        filename: upload.filename,
      });
      onUploaded(created);
      onClose();
    } catch (e) {
      setError(validationErrorMessage(e) ?? 'The referral sources could not be uploaded. Please try again.');
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const dropzoneIcon = useMemo(() => <ClipIcon height={16} />, []);

  return (
    <StandardModal title='Upload Referral Sources' colorTheme={ColorThemeName.Primary} onCancel={onClose}>
      <ModalContent>
        <Intro>
          Attach a .csv file to add referral sources to this client. Use these column headers, in this order:
          <HeaderList>
            {COLUMNS.map(({ header, optional }) => (
              <li key={header}>{optional ? `${header} (Optional)` : header}</li>
            ))}
          </HeaderList>
          Separate multiple Also Known As names with a semicolon (;) or a pipe (|), for example a row of Seattle Grace
          Hospital with SGH; Grace Hospital. Rows that can&apos;t be added, like a source the client already has, are
          skipped and can be downloaded when the upload finishes.{' '}
          <TemplateLink href={TEMPLATE_HREF} download='referral_sources_template.csv'>
            Download a template
          </TemplateLink>
        </Intro>

        <FileDropZone
          onFilesSelected={handleFiles}
          allowedTypes={CSV_TYPES}
          maxFiles={1}
          currentFileCount={upload ? 1 : 0}
          noClick={!!upload}
          noDrag={!!upload}>
          {(dropzone) => (
            <DropArea {...dropzone.getRootProps()} $active={dropzone.isDragActive} $disabled={!!upload}>
              <input {...dropzone.getInputProps()} />
              {dropzoneIcon}
              <DropLabel>
                Drag and drop your file(s) here, or&nbsp;
                <BrowseLink aria-label='launch file browser'>browse your files</BrowseLink>
              </DropLabel>
            </DropArea>
          )}
        </FileDropZone>

        {upload && (
          <AttachedFileRow>
            <AttachedFile
              attachment={upload}
              error={upload.hasError}
              uploadProgress={upload.progress}
              onDeleteAttachment={removeUpload}
              canModifyDocumentType={false}
              DeleteIcon={SmallTrashIcon}
            />
          </AttachedFileRow>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </ModalContent>

      <Actions>
        <Button variant='ghost' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={!canUpload} loading={submitting} onClick={handleUpload}>
          Upload
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Intro = styled.div`
  font-size: 14px;
  color: ${colors.black100};
  line-height: 1.5;
  text-align: left;
`;

const HeaderList = styled.ul`
  margin: 4px 0 8px;
  padding-left: 24px;
`;

const TemplateLink = styled.a`
  color: ${colors.blue100};
  text-decoration: underline;
`;

const DropArea = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 32px 16px;
  border: 1px dashed ${({ $active }) => ($active ? colors.blue100 : colors.black25)};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? colors.blue5 : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-align: center;
`;

const DropLabel = styled.span`
  font-size: 14px;
  color: ${colors.black75};
`;

const BrowseLink = styled.span`
  color: ${colors.blue100};
  text-decoration: underline;
`;

const AttachedFileRow = styled.div`
  margin-top: 16px;
  text-align: left;
`;

const AttachedFile = styled(ExternalAttachment)`
  min-height: 40px;
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: ${colors.red100};
`;
